import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ArrowDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const HeroSlider: React.FC = () => {
  const { user } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    {
      image: '/title_image_1.png',
      alt: 'Durga Puja celebration with traditional decorations and Goddess Durga'
    },
    {
      image: '/title_image_2.png', 
      alt: 'Beautiful Durga Puja pandal and traditional Bengali celebrations'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  const scrollToRegistration = () => {
    if (user) {
      // If user is logged in, navigate to home page
      window.location.href = '/';
    } else {
      // If user is not logged in, navigate to signup
      window.location.href = '/signup';
    }
  };

  return (
    <section id="home" className="relative h-[90vh] sm:h-screen overflow-hidden">
      {/* Image Slider */}
      <div className="relative h-full">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to a placeholder or hide if image fails to load
                console.warn(`Failed to load image: ${slide.image}`);
                e.currentTarget.style.backgroundColor = '#E53E3E';
                e.currentTarget.style.display = 'flex';
                e.currentTarget.style.alignItems = 'center';
                e.currentTarget.style.justifyContent = 'center';
                e.currentTarget.innerHTML = '<span style="color: white; font-size: 18px;">Image Loading...</span>';
              }}
            />
            <div className="absolute inset-0 bg-black/40" />
          </div>
        ))}
      </div>

      {/* Content Overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center text-white px-4 max-w-2xl sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-7xl mx-auto">
          <h1 className="font-runtime text-lg sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 animate-fade-in xl:whitespace-nowrap">
            "Intangible Cultural Heritage of Humanity is our Durga Puja"
          </h1>
          <p className="text-xs sm:text-base mb-2 animate-fade-in opacity-90">— UNESCO</p>
          <p className="font-lucy-rose text-sm sm:text-lg mb-6 sm:mb-8 animate-slide-up max-w-xl sm:max-w-2xl mx-auto">
            Celebrating the world's most vibrant cultural festival through art, community, and heritage
          </p>
          <button
            onClick={scrollToRegistration}
            className="inline-flex items-center px-5 py-3 sm:px-8 sm:py-4 bg-dpn-pink hover:bg-dpn-raspberry text-white font-lucy-rose font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl animate-bounce-gentle"
          >
            {user ? 'Explore Our Committee' : 'Join the Global Network'}
            <ArrowDown className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="hidden sm:flex absolute left-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={nextSlide}
        className="hidden sm:flex absolute right-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-colors ${
              index === currentSlide ? 'bg-dpn-pink' : 'bg-white/50'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;